import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  MarkProposalAsSentInput,
  MarkProposalAsSentMutation,
} from '@/gql/graphql';

const MARK_PROPOSAL_AS_SENT_MUTATION = graphql(`
  mutation MarkProposalAsSent($input: MarkProposalAsSentInput!) {
    markProposalAsSent(input: $input) {
      id
    }
  }
`);

/**
 * Mark a draft Proposal as sent without emailing (e.g. delivered out-of-band).
 * Transitions the Proposal's status and touches Project-level state (pipeline,
 * activity), so both caches are invalidated on success.
 */
const useMarkProposalAsSent = (args?: {
  proposalId?: string;
  projectId?: string;
  options?: UseMutationOptions<
    MarkProposalAsSentMutation,
    unknown,
    MarkProposalAsSentInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: MarkProposalAsSentInput) => {
      return gqlClient.request(MARK_PROPOSAL_AS_SENT_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.project(args.projectId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useMarkProposalAsSent;
